import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader, Table, Group, Menu, Grid, GridCol } from '@mantine/core';
import { useSelector } from 'react-redux';
import {
  addEquipment,
  fetchEquipmentList,
  fetchExistencesReport,
  IEquipmentDetails
} from '../../../libraries/equipment';
import { AppState } from '../../client-redux/reducers';
import { Language } from '../../languages/languageHandler';
import ShowEquipment from '../../components/show-equipment';
import PaginationComponent from '../../components/pagination';
import { ILocation } from '../../../libraries/location';
import { ICurrency } from '../../../libraries/currencies';
import { LoginState } from '../../client-redux/login/reducer';
import { delegationCheck, formatCurrAmount, formatShortTransactionType } from '../../utils/script';
import FilterBar, { FilterBarFieldsData } from '../../components/filterbar/index';
import Header from '../../components/util/header';
import { ReactComponent as CheckIcon } from '../../../assets/img/check.svg';
import { ReactComponent as XIcon } from '../../../assets/img/x.svg';
import { ReactComponent as AlertIcon } from '../../../assets/img/triangle-exclamation-solid.svg';
import { useFitTableRows } from '../../components/util/fit-table-rows-hook';
import { formatDateTimeLabel } from '../../utils/date';
import { useStatusOperDialog, isZarphSuccessResponse, isErrorResponse } from '../../context/status-oper-dialog';
import { IconEdit, IconFileTypePdf } from '@tabler/icons-react';
import { EquipState } from '../../../libraries/zps';
import './styles.scss';

let hideColumns = {
  lastComDate: false,
  lastOper: false,
  lastOperDate: false,
  softVersion: false,
  period: true,
  amount: true,
  safeOccPerc: true,
  existencesReport: true,
  edit: true
};

const Equipments = () => {
  const lang = useSelector<AppState, Language>(state => state.lang.lang);
  const login = useSelector<AppState, LoginState>(state => state.login);
  const region = useSelector<AppState, string>(state => state.login.user?.region || 'UTC');

  const locations = useSelector<AppState, ILocation[]>(state => state.locations.locations);
  const currencies = useSelector<AppState, ICurrency[]>(state => state.currencies.currencies);
  const controller = useRef(new AbortController()).current;
  const navigate = useNavigate();

  const [openDetails, setOpenDetails] = useState<boolean>(false);
  const [activeRow, setActiveRow] = useState<number>();
  const [loading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [offset, setOffset] = useState<number>(0);
  const {
    limit = 10,
    setLimit,
    tableRef
  } = useFitTableRows({
    bufferRows: 1,
    minHeightForDynamic: 400
  });
  const [, setSelectedSid] = useState<number>();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [list, setList] = useState<IEquipmentDetails[]>([]);
  const [menuOpened, setMenuOpened] = useState(false);
  const [menuCoords, setMenuCoords] = useState<{ x: number; y: number } | null>(null);
  const [contextEid, setContextEid] = useState<number | null>(null);
  const [contextSid, setContextSid] = useState<number | null>(null);

  const [filterBarData, setFilterBarData] = useState<FilterBarFieldsData>();

  const { handleApiError } = useStatusOperDialog();

  useEffect(() => {
    if (filterBarData && limit) {
      loadEquipmentList();
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterBarData, limit, offset]);

  const handleFilterBarOnDataChange = (data: FilterBarFieldsData) => {
    setFilterBarData(data);
    changePage(1);
  };

  useEffect(() => {
    handleColumnsToHide();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadEquipmentList = () => {
    if (!filterBarData || !limit) return;

    setLoading(true);
    setList([]);
    setTotalRows(0);

    fetchEquipmentList(controller, filterBarData?.entity || 0, filterBarData?.lid || 0, offset, limit, true)
      .then(response => {
        if (isZarphSuccessResponse(response, [9004])) {
          setList(response?.data?.equipments || []);
          setTotalRows(response?.data?.totalRows || 0);
        } else {
          handleApiError(response);
        }
      })
      .catch(err => {
        handleApiError(err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleSelectEquipment = (eid: number) => {
    setSelectedSid(eid);
  };

  const checkStatusForCell = (statusCode: number) => {
    switch (statusCode) {
      case 0:
        return lang.EQUIPMENT_STATUS_LABEL_NOT_DEFINED;
      case 1:
        return lang.EQUIPMENT_STATUS_LABEL_DEFINED;
      case 2:
        return lang.EQUIPMENT_STATUS_LABEL_INICIALIZED;
      case 3:
        return <Group className="online-status">{lang.EQUIPMENT_STATUS_LABEL_ONLINE}</Group>;
      case 4:
        return <Group className="offline-status">{lang.EQUIPMENT_STATUS_LABEL_OFFLINE}</Group>;
      default:
        return '';
    }
  };

  const checkStatusForRow = (statusCode: number) => {
    switch (statusCode) {
      case 0:
        return false;
      case 1:
        return false;
      case 4:
        return false;
      default:
        return true;
    }
  };

  const checkEquipmentState = (state: EquipState) => {
    switch (state) {
      case EquipState.IN_SERVICE:
        return <CheckIcon className="status-ok" />;
      case EquipState.IN_SERVICE_WITH_WARN:
        return <AlertIcon className="alert-icon" />;
      default:
        return <XIcon className="status-not-ok" />;
    }
  };

  const showEquipment = (eid: number) => {
    if (login.permissions.hasCITPermissions() && !login.permissions.hasFinancialPermissions()) {
      navigate(`/dashboardV2/equipment/${eid}`);
      return;
    }

    setActiveRow(eid);
    setOpenDetails(true);
  };

  const getExistencesReport = async (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    eid: number,
    period: number,
    controller: AbortController
  ) => {
    event.stopPropagation();
    try {
      const response = await fetchExistencesReport(eid, period, controller);
      if (isErrorResponse(response)) {
        handleApiError(response);
        return;
      }
    } catch (err: any) {
      handleApiError(err);
    }
  };

  const handleEquipmentPeriod = (period: number) => {
    if (!period || 0) return;

    return period;
  };

  const changePage = (page: number) => {
    const newOffset = (page - 1) * limit;
    setOffset(newOffset);
    setCurrentPage(page);
  };

  const resultsSelect = (newValue: number) => {
    const newLimit = Number(newValue);
    setLimit(newLimit);
    setCurrentPage(1);
    setOffset(0);
  };

  const handleColumnsToHide = () => {
    hideColumns.lastComDate = false;
    hideColumns.lastOper = false;
    hideColumns.lastOperDate = false;
    hideColumns.softVersion = false;
    hideColumns.period = true;
    hideColumns.amount = true;
    hideColumns.safeOccPerc = true;
    hideColumns.existencesReport = true;
    hideColumns.edit = true;

    if (login.permissions.hasFinancialPermissions()) {
      hideColumns.period = false;
      hideColumns.amount = false;
      //hideColumns.existencesReport = false;
      hideColumns.safeOccPerc = false;
    }

    if (login.permissions.hasSystemConfigPermissions()) {
      hideColumns.edit = false;
    }

    if (login.permissions.hasCITPermissions() && !login.permissions.hasFinancialPermissions()) {
      hideColumns.lastComDate = true;
      hideColumns.lastOper = true;
      hideColumns.lastOperDate = true;
      hideColumns.softVersion = true;
      hideColumns.safeOccPerc = false;
      hideColumns.period = true;
    }
  };

  const handleRowRightClick = (event: React.MouseEvent, selectedEid: number, selectedSid: number, etid: number) => {
    event.preventDefault();

    const restrictedEtids = [404, 412, 442, 654];
    if (restrictedEtids.includes(etid)) {
      return;
    }

    setContextEid(selectedEid);
    setContextSid(selectedSid);

    const menuWidth = 200;
    const menuHeight = 300;
    const maxX = window.innerWidth - menuWidth;
    const maxY = window.innerHeight - menuHeight;

    const x = event.clientX > maxX ? maxX : event.clientX;
    const y = event.clientY > maxY ? maxY : event.clientY;

    setMenuCoords({ x, y });
    setMenuOpened(true);
  };

  const formatSafeOccPerc = (safeOccPerc?: number) => {
    if (safeOccPerc === undefined || safeOccPerc === null) return '';

    return safeOccPerc + '%';
  };

  const handleAddEquipmentToList = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.txt,.csv,.json';
    input.onchange = async (event: any) => {
      const file = event.target.files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = async e => {
        let equipmentFile = e.target?.result as string;
        const base64Prefix = /^data:.*;base64,/;
        if (base64Prefix.test(equipmentFile)) {
          equipmentFile = equipmentFile.replace(base64Prefix, '');
        }
        try {
          const response = await addEquipment({ entid: login.user.entid, equipmentFile }, controller);
          if (isErrorResponse(response) || response?.data.statusOper?.code !== 0) {
            handleApiError(response);
            return;
          }
          loadEquipmentList();
        } catch (error: any) {
          handleApiError(error);
        }
      };
      reader.readAsDataURL(file);
    };
    input.click();
  };

  return (
    <div className="equipment">
      {openDetails && activeRow && lang && (
        <ShowEquipment eid={activeRow} openDetails={openDetails} hideBtn={() => setOpenDetails(false)} lang={lang} />
      )}
      <Header pageTitle={lang.GLOBAL_EQUIPMENTS} />
      <FilterBar
        onSearch={loadEquipmentList}
        onDataChange={handleFilterBarOnDataChange}
        fieldsUsage={{
          entity: true,
          lid: true
        }}
        addButton={login.permissions.hasSystemConfigPermissions() ? handleAddEquipmentToList : undefined}
      />

      <Table.ScrollContainer minWidth={500} ref={tableRef}>
        <Table striped highlightOnHover verticalSpacing="sm">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>EID</Table.Th>
              <Table.Th>{lang.GLOBAL_TYPE}</Table.Th>
              <Table.Th>SN</Table.Th>
              <Table.Th>{lang.EQUIPMENT_DESCRIPTION}</Table.Th>
              <Table.Th>{lang.EQUIPMENT_LOCAL}</Table.Th>
              <Table.Th>{lang.EQUIPMENT_COM_STATE}</Table.Th>
              <Table.Th>{lang.EQUIPMENT_STATE_MIN}</Table.Th>
              {!hideColumns.lastComDate && <Table.Th>{lang.EQUIPMENT_COM_DATE_MIN}</Table.Th>}
              {!hideColumns.lastOper && <Table.Th>{lang.EQUIPMENT_LAST_OPER_MIN}</Table.Th>}
              {!hideColumns.lastOperDate && <Table.Th>{lang.EQUIPMENT_LAST_OPER_DT_MIN}</Table.Th>}
              {!hideColumns.softVersion && <Table.Th>{lang.EQUIPMENT_SOFTWARE_VERSION_MIN}</Table.Th>}
              {!hideColumns.period && <Table.Th>{lang.GLOBAL_PERIOD}</Table.Th>}
              {!hideColumns.amount && <Table.Th>{lang.GLOBAL_AMOUNT}</Table.Th>}
              {!hideColumns.safeOccPerc && <Table.Th>{lang.EQUIPMENT_SAFE_OCC_PERC_MIN}</Table.Th>}
              {!hideColumns.existencesReport && <Table.Th></Table.Th>}
              {!hideColumns.edit && <Table.Th></Table.Th>}
            </Table.Tr>
          </Table.Thead>

          <Table.Tbody>
            {loading ? (
              <Table.Tr>
                <Table.Td colSpan={16}>
                  <Loader color="#016273" />
                </Table.Td>
              </Table.Tr>
            ) : (
              <>
                {!list.length ? (
                  <Table.Tr>
                    <Table.Td colSpan={16} style={{ textAlign: 'center' }}>
                      {lang.GLOBAL_NO_RESULTS}
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  list?.map((equipment, index) => {
                    const matchingCurrency = currencies?.find(c => c.cid === equipment?.periodAmounts?.[0]?.cid);

                    return (
                      <Table.Tr
                        key={index}
                        onClick={() => {
                          if (checkStatusForRow(equipment.equipment.status)) {
                            handleSelectEquipment(equipment.equipment.sid);
                            showEquipment(equipment.equipment.eid);
                          }
                        }}
                        onContextMenu={event =>
                          handleRowRightClick(
                            event,
                            equipment.equipment.eid,
                            equipment.equipment.sid,
                            equipment.equipment.etid
                          )
                        }
                        style={{
                          cursor:
                            checkStatusForRow(equipment.equipment.status) &&
                            ![404, 412, 442, 654].includes(equipment.equipment.etid)
                              ? 'pointer'
                              : 'default'
                        }}
                        className={checkStatusForRow(equipment.equipment.status) ? 'online-row' : 'offline-row'}
                      >
                        <Table.Td>{equipment.equipment.eid}</Table.Td>
                        <Table.Td>{equipment.equipment.etid}</Table.Td>
                        <Table.Td>{equipment.equipment.serialnumber}</Table.Td>
                        <Table.Td>{equipment.equipment.description}</Table.Td>
                        <Table.Td>{delegationCheck(equipment.equipment.sid, locations)}</Table.Td>
                        <Table.Td>{checkStatusForCell(equipment.equipment.status)}</Table.Td>
                        <Table.Td>{checkEquipmentState(equipment.workingState)}</Table.Td>
                        {!hideColumns.lastComDate && (
                          <Table.Td>{formatDateTimeLabel(equipment.lastComDt, region)}</Table.Td>
                        )}
                        {!hideColumns.lastOper && (
                          <Table.Td>{formatShortTransactionType(lang, equipment.lastOperation)}</Table.Td>
                        )}
                        {!hideColumns.lastOperDate && (
                          <Table.Td>{formatDateTimeLabel(equipment.lastOperationDt, region)}</Table.Td>
                        )}
                        {!hideColumns.softVersion && <Table.Td>{equipment.softwareVersion}</Table.Td>}
                        {!hideColumns.period && <Table.Td>{handleEquipmentPeriod(equipment.period)}</Table.Td>}
                        {!hideColumns.amount && (
                          <Table.Td>
                            {matchingCurrency
                              ? formatCurrAmount(equipment?.periodAmounts?.[0]?.total || 0, matchingCurrency)
                              : ''}
                          </Table.Td>
                        )}
                        {!hideColumns.safeOccPerc && <Table.Td>{formatSafeOccPerc(equipment.safeOccPerc)}</Table.Td>}
                        {!hideColumns.existencesReport && (
                          <Table.Td>
                            {equipment.period ? (
                              <div
                                onClick={event =>
                                  getExistencesReport(event, equipment.equipment.eid, equipment.period, controller)
                                }
                              >
                                <IconFileTypePdf stroke={2} style={{ cursor: 'pointer' }} />
                              </div>
                            ) : (
                              ''
                            )}
                          </Table.Td>
                        )}
                        {!hideColumns.edit && (
                          <Table.Td>
                            <IconEdit
                              stroke={2}
                              style={{ cursor: 'pointer' }}
                              onClick={event => {
                                event.stopPropagation();
                                navigate(`/equipmentConfig/${equipment.equipment.eid}`);
                              }}
                            />
                          </Table.Td>
                        )}
                      </Table.Tr>
                    );
                  })
                )}
              </>
            )}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      <Grid gap="sm" classNames={{ root: 'iconography' }}>
        <GridCol>
          <CheckIcon className="status-ok" />
          <span>{lang.EQUIPMENT_STATE_BUTTON_IN_SERVICE}</span>
        </GridCol>

        <GridCol>
          <XIcon className="status-not-ok" />
          <span>{lang.OUT_OF_SERVICE}</span>
        </GridCol>

        <GridCol>
          <AlertIcon className="alert-icon" />
          <span>{lang.EQUIPMENT_STATE_IN_SERVICE_WITH_ALERTS}</span>
        </GridCol>
      </Grid>

      {menuOpened && menuCoords && (
        <Menu
          opened={true}
          onClose={() => setMenuOpened(false)}
          styles={{
            dropdown: {
              position: 'absolute',
              top: `${menuCoords.y}px`,
              left: `${menuCoords.x}px`
            }
          }}
        >
          <Menu.Target>
            <div
              style={{
                position: 'absolute',
                top: `${menuCoords.y}px`,
                left: `${menuCoords.x}px`,
                width: 1,
                height: 1
              }}
            />
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item
              onClick={() =>
                navigate(`/equipment/currency/read/${contextEid}/${contextSid}`, {
                  state: { contextSid }
                })
              }
            >
              {lang.EQUIPMENT_CONSULT_WORKING_CAPITAL}
            </Menu.Item>

            {(login?.permissions?.hasFinancialPermissions() || login?.permissions?.hasSystemConfigPermissions()) && (
              <Menu.Item
                onClick={() =>
                  navigate(`/equipment/currency/edit/${contextEid}/${contextSid}`, {
                    state: { contextSid }
                  })
                }
              >
                {lang.EQUIPMENT_EDIT_WORKING_CAPITAL}
              </Menu.Item>
            )}
            {login?.permissions?.hasSystemConfigPermissions() && login?.settings?.useDispensers && (
              <Menu.Item
                onClick={() =>
                  navigate(`/dispenser/${contextEid}`, {
                    state: { contextEid }
                  })
                }
              >
                {lang.EQUIPMENT_CONSULT_DISPENSERS}
              </Menu.Item>
            )}
          </Menu.Dropdown>
        </Menu>
      )}

      <Group>
        <PaginationComponent
          total={list?.length ? Math.ceil(list.length / limit) : totalRows}
          itemsPerPage={limit}
          dynamicLimit={limit}
          currentPage={currentPage}
          onPageChange={changePage}
          onResultsChange={resultsSelect}
          offset={offset}
        />
      </Group>
    </div>
  );
};

export default Equipments;
